import { useEffect, useMemo, useRef, useState } from 'react';
import { Sheet } from './ui/Sheet';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { normalizeInvoiceIssuer } from '../constants/invoiceIssuer';
import { buildInvoiceData, zipInvoicePdfs } from '../utils/invoiceGenerator';
import { formatSubscriptionDate, getAcademicYearPeriod } from '../utils/academicYear';

function defaultBulkDates(heads) {
  const today = new Date().toISOString().slice(0, 10);
  const ref = heads?.find((h) => h.subscription_expires_at) || heads?.[0];
  const period = getAcademicYearPeriod(ref?.subscription_expires_at);
  return {
    invoiceDate: today,
    periodStart: period.periodStart,
    periodEnd: period.periodEnd,
  };
}

export function BulkInvoiceSheet({
  open,
  onClose,
  heads,
  labels,
  savedIssuer,
  fetchInvoiceIssuer,
  onDone,
}) {
  const [issuer, setIssuer] = useState(() => normalizeInvoiceIssuer(savedIssuer));
  const [invoiceDate, setInvoiceDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [lineRate, setLineRate] = useState('');
  const [lineDescription, setLineDescription] = useState('');
  const [generating, setGenerating] = useState(false);
  const fetchRef = useRef(fetchInvoiceIssuer);
  fetchRef.current = fetchInvoiceIssuer;

  useEffect(() => {
    if (!open || !heads?.length) return;

    const dates = defaultBulkDates(heads);
    setInvoiceDate(dates.invoiceDate);
    setPeriodStart(dates.periodStart);
    setPeriodEnd(dates.periodEnd);

    const normalized = normalizeInvoiceIssuer(savedIssuer);
    setIssuer(normalized);
    setLineRate(String(normalized.pricePerClassYear));
    setLineDescription(normalized.lineItemDescription);

    let cancelled = false;
    (async () => {
      const loaded = await fetchRef.current();
      if (!cancelled && loaded) {
        const next = normalizeInvoiceIssuer(loaded);
        setIssuer(next);
        setLineRate(String(next.pricePerClassYear));
        setLineDescription(next.lineItemDescription);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const eligibleHeads = useMemo(
    () => (heads || []).filter((h) => (parseInt(h.divisions, 10) || 0) > 0),
    [heads]
  );

  const skippedCount = (heads?.length || 0) - eligibleHeads.length;

  const previews = useMemo(() => {
    const issuerOpts = {
      issuer: { ...issuer, lineItemDescription: lineDescription, pricePerClassYear: lineRate },
      invoiceDate,
      periodStart,
      periodEnd,
      lineRate,
      lineDescription,
    };
    return eligibleHeads.map((head) => ({
      head,
      data: buildInvoiceData(head, issuerOpts),
    }));
  }, [eligibleHeads, issuer, invoiceDate, periodStart, periodEnd, lineRate, lineDescription]);

  const grandTotal = previews.reduce((sum, { data }) => sum + (data.total || 0), 0);

  const handleGenerate = async () => {
    if (previews.length === 0) return;
    setGenerating(true);
    try {
      const normalizedIssuer = normalizeInvoiceIssuer({
        ...issuer,
        lineItemDescription: lineDescription,
        pricePerClassYear: lineRate,
      });
      const invoices = previews.map(({ data }) => ({
        ...data,
        issuer: normalizedIssuer,
      }));
      await zipInvoicePdfs(invoices);
      onDone?.({ generated: invoices.length, skipped: skippedCount });
      onClose();
    } finally {
      setGenerating(false);
    }
  };

  if (!heads?.length) return null;

  return (
    <Sheet open={open} onClose={onClose} title={labels.bulkInvoiceTitle}>
      <div className="space-y-5">
        <div className="rounded-lg border border-border bg-surface px-3 py-2 text-sm">
          <p className="font-medium text-text">
            {labels.bulkInvoiceSchools} ({heads.length})
          </p>
          {skippedCount > 0 && (
            <p className="mt-1 text-xs text-warning">
              {skippedCount} {labels.toastInvoiceSkippedNoDivisions}
            </p>
          )}
        </div>

        <ul className="max-h-40 space-y-1 overflow-y-auto rounded-lg border border-border px-3 py-2 text-sm">
          {heads.map((h) => {
            const divs = parseInt(h.divisions, 10) || 0;
            const rate = parseFloat(lineRate) || issuer.pricePerClassYear;
            return (
              <li key={h.id} className="flex justify-between gap-2">
                <span className="min-w-0 truncate text-text">{h.name}</span>
                <span className={`shrink-0 tabular-nums ${divs <= 0 ? 'text-muted line-through' : 'text-muted'}`}>
                  {divs} × Rs. {rate.toLocaleString('en-IN')}
                </span>
              </li>
            );
          })}
        </ul>

        <div className="space-y-3 border-t border-border pt-4">
          <p className="text-sm font-semibold text-text">{labels.invoiceLineItem}</p>
          <Input
            label={labels.invoiceLineDescription}
            value={lineDescription}
            onChange={(e) => setLineDescription(e.target.value)}
          />
          <Input
            label={labels.invoiceRatePerClass}
            type="number"
            min={1}
            value={lineRate}
            onChange={(e) => setLineRate(e.target.value)}
          />
          {eligibleHeads.length > 0 && (
            <p className="text-sm font-semibold text-primary">
              {labels.bulkInvoiceGrandTotal}: Rs. {grandTotal.toLocaleString('en-IN')}
            </p>
          )}
        </div>

        <div className="space-y-3 border-t border-border pt-4">
          <p className="text-sm font-semibold text-text">{labels.invoicePeriod}</p>
          <p className="text-xs text-muted">{labels.bulkInvoicePeriodHint}</p>
          <Input
            label={labels.invoiceDate}
            type="date"
            value={invoiceDate}
            onChange={(e) => setInvoiceDate(e.target.value)}
          />
          <div className="grid grid-cols-2 gap-3">
            <Input
              label={labels.invoicePeriodStart}
              type="date"
              value={periodStart}
              onChange={(e) => setPeriodStart(e.target.value)}
            />
            <Input
              label={labels.invoicePeriodEnd}
              type="date"
              value={periodEnd}
              onChange={(e) => setPeriodEnd(e.target.value)}
            />
          </div>
          {periodStart && periodEnd && (
            <p className="text-xs text-muted">
              {formatSubscriptionDate(periodStart)} – {formatSubscriptionDate(periodEnd)}
            </p>
          )}
        </div>

        <div className="flex gap-2 pt-2">
          <Button variant="secondary" className="flex-1" onClick={onClose}>
            {labels.cancel}
          </Button>
          <Button
            className="flex-1"
            onClick={handleGenerate}
            disabled={eligibleHeads.length === 0}
            isLoading={generating}
          >
            {labels.bulkInvoiceDownloadZip} ({eligibleHeads.length})
          </Button>
        </div>
        {eligibleHeads.length === 0 && (
          <p className="text-xs text-danger">{labels.invoiceNoDivisions}</p>
        )}
      </div>
    </Sheet>
  );
}
